import { useEffect, useState } from 'react';

/**
 * Read a design token as a real colour value.
 *
 * WHY THIS EXISTS
 * Recharts sets fill / stroke / stopColor as SVG PRESENTATION ATTRIBUTES, and
 * var() does not resolve in an attribute — only in a CSS declaration. So
 *
 *     <Bar fill="hsl(var(--primary))" />
 *
 * renders a bar with no fill at all. That is why every chart in this codebase
 * ended up with a hardcoded hex: the obvious way to use a token there is silently
 * broken, and the failure looks like a styling mistake rather than a mechanism
 * mismatch. This reads the token off :root and returns something an attribute can
 * actually use.
 *
 * Tokens come in two shapes (TOKEN_CONTRACT §1 rule 4): brand and status tokens
 * are bare HSL triplets, surface tokens are hex. Both are handled.
 */
const readRaw = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const TRIPLET = /^[0-9.]+(deg)?[ ,]+[0-9.]+%[ ,]+[0-9.]+%$/;

export function tokenColor(name, alpha = 1) {
  if (typeof document === 'undefined') return 'transparent';
  const raw = readRaw(name);
  if (!raw) return 'transparent';
  if (TRIPLET.test(raw)) return alpha === 1 ? 'hsl(' + raw + ')' : 'hsl(' + raw + ' / ' + alpha + ')';
  return raw;
}

/**
 * The same thing, but re-read when the palette moves underneath it — a theme
 * toggle rewrites data-theme, and per-institute branding (TOKEN_CONTRACT §5)
 * sets the brand properties inline on the same element. Without the observer a
 * chart keeps the colours it was born with while the page around it changes.
 *
 *   const colors = useTokenColors({ bar: '--primary', bad: '--danger' });
 *   <Bar fill={colors.bar} />
 */
export function useTokenColors(spec) {
  const key = JSON.stringify(spec);
  const resolve = () => {
    const out = {};
    for (const [k, v] of Object.entries(spec)) {
      out[k] = Array.isArray(v) ? tokenColor(v[0], v[1]) : tokenColor(v);
    }
    return out;
  };
  const [colors, setColors] = useState(resolve);

  useEffect(() => {
    setColors(resolve());
    const observer = new MutationObserver(() => setColors(resolve()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'style', 'class'],
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return colors;
}

export default tokenColor;
